import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoTooltip from './InfoTooltip';
import './BalenaEtcherDownload.css';

function BalenaEtcherDownload({ selectedOS }) {
  const { t } = useTranslation();

  // Balena Etcher download URLs
  const downloadUrls = {
    windows: {
      'x86|x64': 'https://github.com/balena-io/etcher/releases/download/v2.1.4/balenaEtcher-2.1.4.Setup.exe'
    },
    mac: {
      'x64': 'https://github.com/balena-io/etcher/releases/download/v2.1.4/balenaEtcher-2.1.4-x64.dmg',
      'arm64': 'https://github.com/balena-io/etcher/releases/download/v2.1.4/balenaEtcher-2.1.4-arm64.dmg'
    },
    linux: {
      'x64': 'https://github.com/balena-io/etcher/releases/download/v2.1.4/balenaEtcher-linux-x64-2.1.4.zip',
      'x86': 'https://github.com/balena-io/etcher/releases/download/v1.7.9/balenaEtcher-1.7.9-ia32.AppImage'
    }
  };

  const getOSKey = () => {
    if (selectedOS === 'Windows') return 'windows';
    if (selectedOS === 'Mac') return 'mac';
    if (selectedOS === 'Linux') return 'linux';
    return null;
  };

  const osKey = getOSKey();
  const fallbackUrl = 'https://etcher.balena.io/#download-etcher';

  const handleDownload = (arch = null) => {
    if (!osKey) {
      window.open(fallbackUrl, '_blank');
      return;
    }

    if (osKey === 'windows') {
      // Windows has only one download option
      window.open(downloadUrls.windows['x86|x64'], '_blank');
    } else if (arch) {
      // Mac or Linux with selected architecture
      window.open(downloadUrls[osKey][arch], '_blank');
    } else {
      // No architecture selected yet, show options
      // Architecture selection is handled by button clicks
    }
  };

  const getOSDisplayName = () => {
    if (selectedOS === 'Windows') return t("pathBuilder.checkboxes.windows");
    if (selectedOS === 'Mac') return t("pathBuilder.checkboxes.mac");
    if (selectedOS === 'Linux') return t("pathBuilder.checkboxes.linux");
    return t("pathBuilder.instructionContent.yourOperatingSystem");
  };

  // If no OS selected, show fallback
  if (!osKey) {
    return (
      <div className="balena-etcher-download">
        <a 
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="balena-download-button"
        >
          {t("pathBuilder.instructionContent.downloadBalenaEtcher")}
        </a>
        <p className="balena-download-note">
          {t("pathBuilder.instructionContent.downloadBalenaEtcherManualNote")}
        </p>
      </div>
    );
  }

  // Windows - single download option
  if (osKey === 'windows') {
    return (
      <div className="balena-etcher-download">
        <button 
          onClick={() => handleDownload()}
          className="balena-download-button"
        >
          {t("pathBuilder.instructionContent.downloadBalenaEtcher")} ({getOSDisplayName()})
        </button>
        <p className="balena-download-note">
          {t("pathBuilder.instructionContent.downloadBalenaEtcherNote", { 
            os: getOSDisplayName(),
            site: "balena.io"
          })}
        </p>
        <a 
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="balena-official-link"
        >
          {t("pathBuilder.instructionContent.visitOfficialDownloadPage")} →
        </a>
      </div>
    );
  }

  // Mac or Linux - show architecture options
  return (
    <div className="balena-etcher-download">
      <p className="balena-download-label">
        {t("pathBuilder.instructionContent.selectArchitecture")} ({getOSDisplayName()}):
        <InfoTooltip contentKey={`pathBuilder.instructionContent.howToFindArchitecture${osKey === 'mac' ? 'Mac' : 'Linux'}`} />
      </p>
      
      {osKey === 'mac' && (
        <div className="balena-arch-options">
          <button 
            onClick={() => handleDownload('x64')}
            className="balena-arch-button"
          >
            {t("pathBuilder.instructionContent.downloadBalenaEtcher")} - {getOSDisplayName()} (x64)
          </button>
          <button 
            onClick={() => handleDownload('arm64')}
            className="balena-arch-button"
          >
            {t("pathBuilder.instructionContent.downloadBalenaEtcher")} - {getOSDisplayName()} (ARM64)
          </button>
        </div>
      )}

      {osKey === 'linux' && (
        <div className="balena-arch-options">
          <button 
            onClick={() => handleDownload('x64')}
            className="balena-arch-button"
          >
            {t("pathBuilder.instructionContent.downloadBalenaEtcher")} - {getOSDisplayName()} (64-bit)
          </button>
          <button 
            onClick={() => handleDownload('x86')}
            className="balena-arch-button"
          >
            {t("pathBuilder.instructionContent.downloadBalenaEtcher")} - {getOSDisplayName()} (32-bit Legacy)
          </button>
        </div>
      )}

      <p className="balena-download-note">
        {t("pathBuilder.instructionContent.downloadBalenaEtcherNote", { 
          os: getOSDisplayName(),
          site: "balena.io"
        })}
      </p>
      
      <a 
        href={fallbackUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="balena-official-link"
      >
        {t("pathBuilder.instructionContent.visitOfficialDownloadPage")} →
      </a>
    </div>
  );
}

export default BalenaEtcherDownload;

